import Decimal from "decimal.js";

import type { CustomerMinimal, Suggestion } from "../state/types";
import {
  PHRASE_BONUS,
  SCORE_WEIGHT_COVERAGE,
  SCORE_WEIGHT_EDIT_SIMILARITY,
  SCORE_WEIGHT_ORDER,
  TOP_SUGGESTIONS_PER_FILE,
} from "./config";
import { normalizeForMatch, stripLegalSuffixes } from "./tokenize";

const ZERO = new Decimal(0);
const ONE = new Decimal(1);

type ScoreBreakdown = {
  score: Decimal;
  coverage: Decimal;
  order: Decimal;
  edit_similarity: Decimal;
  phrase_bonus_hit: boolean;
};

/**
 * Levenshtein distance between two strings (integer character ops).
 *
 * Plain integer arithmetic is used here; the resulting distance is fed into
 * Decimal-based similarity math at the call site.
 */
function levenshteinDistance(left: string, right: string): number {
  const leftLen = left.length;
  const rightLen = right.length;
  if (leftLen === 0) return rightLen;
  if (rightLen === 0) return leftLen;

  let previous: number[] = new Array(rightLen + 1);
  let current: number[] = new Array(rightLen + 1);
  for (let j = 0; j <= rightLen; j += 1) previous[j] = j;

  for (let i = 1; i <= leftLen; i += 1) {
    current[0] = i;
    const leftCh = left.charCodeAt(i - 1);
    for (let j = 1; j <= rightLen; j += 1) {
      const cost = leftCh === right.charCodeAt(j - 1) ? 0 : 1;
      const deletion = previous[j] + 1;
      const insertion = current[j - 1] + 1;
      const substitution = previous[j - 1] + cost;
      let best = deletion;
      if (insertion < best) best = insertion;
      if (substitution < best) best = substitution;
      current[j] = best;
    }
    const swap = previous;
    previous = current;
    current = swap;
  }

  return previous[rightLen];
}

/**
 * Normalized Levenshtein similarity:
 *   1 - distance / max(left.length, right.length)
 *
 * Returns 1 when both strings are empty.
 */
function normalizedLevenshteinSimilarity(left: string, right: string): Decimal {
  const maxLen = Math.max(left.length, right.length);
  if (maxLen === 0) return ONE;
  const distance = new Decimal(levenshteinDistance(left, right));
  return ONE.minus(distance.div(maxLen));
}

/**
 * Weighted percent of customer tokens found in file tokens.
 * Each customer token contributes its character length to the totals;
 * the file token set is checked once per customer token (multiplicity in
 * the filename does not increase coverage).
 */
function computeCoverage(
  fileTokens: string[],
  customerTokens: string[],
): Decimal {
  if (customerTokens.length === 0) return ZERO;
  const fileTokenSet = new Set<string>(fileTokens);
  let totalWeight = ZERO;
  let matchedWeight = ZERO;
  for (const token of customerTokens) {
    const weight = new Decimal(token.length);
    totalWeight = totalWeight.plus(weight);
    if (fileTokenSet.has(token)) {
      matchedWeight = matchedWeight.plus(weight);
    }
  }
  if (totalWeight.isZero()) return ZERO;
  return matchedWeight.div(totalWeight);
}

/**
 * Percent of customer tokens that appear in the same relative order
 * inside the file token sequence.
 *
 * Implemented as the length of the longest in-order subsequence of
 * `customerTokens` that can be found within `fileTokens`, divided by
 * `customerTokens.length`.
 */
function computeOrder(
  fileTokens: string[],
  customerTokens: string[],
): Decimal {
  if (customerTokens.length === 0) return ZERO;
  let fileCursor = 0;
  let matched = 0;
  for (const customerToken of customerTokens) {
    while (fileCursor < fileTokens.length) {
      if (fileTokens[fileCursor] === customerToken) {
        matched += 1;
        fileCursor += 1;
        break;
      }
      fileCursor += 1;
    }
    if (fileCursor >= fileTokens.length) break;
  }
  return new Decimal(matched).div(customerTokens.length);
}

/**
 * Best normalized Levenshtein similarity between the customer phrase and
 * any contiguous same-sized token window in the filename. When the file
 * has fewer tokens than the customer, fall back to comparing the entire
 * joined filename phrase against the customer phrase.
 */
function computeEditSimilarity(
  fileTokens: string[],
  customerTokens: string[],
  customerPhrase: string,
  filePhrase: string,
): Decimal {
  if (customerTokens.length === 0) return ZERO;
  if (fileTokens.length < customerTokens.length) {
    return normalizedLevenshteinSimilarity(customerPhrase, filePhrase);
  }
  let best = ZERO;
  const windowSize = customerTokens.length;
  const lastStart = fileTokens.length - windowSize;
  for (let start = 0; start <= lastStart; start += 1) {
    const window = fileTokens.slice(start, start + windowSize).join(" ");
    const similarity = normalizedLevenshteinSimilarity(customerPhrase, window);
    if (similarity.greaterThan(best)) {
      best = similarity;
    }
  }
  return best;
}

function buildReason(breakdown: ScoreBreakdown): string {
  if (breakdown.phrase_bonus_hit) return "exact phrase in filename";
  if (breakdown.coverage.greaterThanOrEqualTo("0.9")) {
    return "all customer name tokens present";
  }
  if (breakdown.edit_similarity.greaterThanOrEqualTo("0.85")) {
    return "near-spelling match of customer name";
  }
  if (breakdown.coverage.greaterThanOrEqualTo("0.6")) {
    return "most customer name tokens present";
  }
  return "partial match";
}

/**
 * Score a single filename against a single customer long name.
 *
 * Returns the score as a `number` in the closed interval [0, 1], plus a
 * short human-readable reason describing why the score is what it is.
 * All internal arithmetic uses Decimal; the conversion to `number` happens
 * only at the return boundary.
 */
export function scoreFileAgainstCustomer(
  fileName: string,
  customerLongName: string,
): { score: number; reason: string } {
  const fileTokensRaw = normalizeForMatch(fileName);
  const customerTokensRaw = normalizeForMatch(customerLongName);
  const fileTokens = stripLegalSuffixes(fileTokensRaw);
  const customerTokens = stripLegalSuffixes(customerTokensRaw);

  if (fileTokens.length === 0 || customerTokens.length === 0) {
    return { score: 0, reason: "no tokens to compare" };
  }

  const filePhrase = fileTokens.join(" ");
  const customerPhrase = customerTokens.join(" ");

  const coverage = computeCoverage(fileTokens, customerTokens);
  const order = computeOrder(fileTokens, customerTokens);
  const editSimilarity = computeEditSimilarity(
    fileTokens,
    customerTokens,
    customerPhrase,
    filePhrase,
  );

  const weighted = coverage
    .times(SCORE_WEIGHT_COVERAGE)
    .plus(editSimilarity.times(SCORE_WEIGHT_EDIT_SIMILARITY))
    .plus(order.times(SCORE_WEIGHT_ORDER));

  const phraseBonusHit = filePhrase.includes(customerPhrase);
  const phraseBonusValue = new Decimal(PHRASE_BONUS);
  const finalScore = phraseBonusHit
    ? Decimal.max(phraseBonusValue, weighted)
    : weighted;

  const breakdown: ScoreBreakdown = {
    score: finalScore,
    coverage,
    order,
    edit_similarity: editSimilarity,
    phrase_bonus_hit: phraseBonusHit,
  };

  return {
    score: finalScore.toNumber(),
    reason: buildReason(breakdown),
  };
}

/**
 * Score a filename against every customer and return the top suggestions.
 *
 * Sorted by score descending. Ties are broken by customer id ascending so
 * the ordering is deterministic. At most `TOP_SUGGESTIONS_PER_FILE`
 * entries are returned. Customers that score 0 are filtered out.
 */
export function scoreFileAgainstCustomers(
  fileName: string,
  customers: CustomerMinimal[],
): Suggestion[] {
  const all: Suggestion[] = [];
  for (const customer of customers) {
    const { score, reason } = scoreFileAgainstCustomer(
      fileName,
      customer.long_name,
    );
    if (score <= 0) continue;
    all.push({
      customer_id: customer.id,
      score,
      reason,
    });
  }
  all.sort((a: Suggestion, b: Suggestion) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.customer_id < b.customer_id) return -1;
    if (a.customer_id > b.customer_id) return 1;
    return 0;
  });
  return all.slice(0, TOP_SUGGESTIONS_PER_FILE);
}
